import { useCallback } from "react"
import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { AxiosInstance } from "axios"
import JSZip from "jszip"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import {
  getAutomationScripts,
  getAutomationScriptMetadata,
  runAutomationScript,
  getJobStatus,
  cancelJob,
} from "api/Repos/automation"
import {
  documentUpload,
  documentDelete,
  documentFetch,
  documentFolderCreate,
} from "api/Repos/documents"
import { useProjectStore } from "stores/projectStore"
import { notify } from "shared/ui/Notifications/notify"
import { downloadDataAsFile } from "utils/files/downloadUtils"
import { isJobCompleted } from "utils/automationUtils"
import { JOB_STATUS, JobStatusResponse } from "types/automation.types"

/**
 * Export scripts: a unit's own automation scripts (named export_<something>) that reshape the
 * normal CSV export into a client's layout. The CSV export lists them as extra categorical
 * formats. The export's data and map go to the project's documents, the script runs on them and
 * names the files it wrote, and those come back as the download. Nothing is left in the documents.
 */

const EXPORT_SCRIPT_PREFIX = "export_"
// A script's value among the categorical formats, so it never clashes with a CSV_CAT_OPTION
const SCRIPT_OPTION_PREFIX = "script:"
// The documents folder the files go through (where automation scripts write)
export const EXPORT_SCRIPT_FOLDER = "Automation_Output"
const POLL_INTERVAL_MS = 1_000
const TIMEOUT_MS = 10 * 60_000

export type ExportScriptOption = `script:${string}`

export interface ExportScript {
  name: string
  label: string
}

export interface ExportScriptResult {
  /** The zip that was downloaded (what the project history records). */
  file: File
  /** The script's WARNING lines. */
  warnings: string[]
}

/** Whether an automation script is an export script (export_ prefix, any case; not in a subfolder). */
export const isExportScriptName = (name: string): boolean =>
  name.toLowerCase().startsWith(EXPORT_SCRIPT_PREFIX) && !name.includes("\\")

/** The label when the script's metadata gives none: the name without its export_ prefix. */
export const exportScriptFallbackLabel = (name: string): string =>
  name.slice(EXPORT_SCRIPT_PREFIX.length)

export const toScriptOption = (name: string): ExportScriptOption =>
  `${SCRIPT_OPTION_PREFIX}${name}`

/** The script name of a categorical format value, or null when the value is a normal format. */
export const scriptNameFromOption = (value: string): string | null =>
  value.startsWith(SCRIPT_OPTION_PREFIX) ? value.slice(SCRIPT_OPTION_PREFIX.length) : null

/**
 * The unit's export scripts with their labels. A script whose metadata can't be read keeps the
 * fallback label.
 */
export const loadExportScripts = async (axios: AxiosInstance): Promise<ExportScript[]> => {
  const response = await getAutomationScripts(axios)
  const names = (response.data?.result?.private ?? []).filter(isExportScriptName)
  const metadata = await Promise.allSettled(
    names.map((name) => getAutomationScriptMetadata(axios, name))
  )
  return names.map((name, i) => {
    const result = metadata[i]
    const label = result.status === "fulfilled" ? result.value.data?.label : undefined
    return {
      name,
      label: typeof label === "string" && label.trim() ? label.trim() : exportScriptFallbackLabel(name),
    }
  })
}

/** The lines a script's output reports, by kind. */
export const parseScriptOutput = (
  stdout: string = ""
): { outputs: string[]; warnings: string[]; errors: string[] } => {
  const outputs: string[] = []
  const warnings: string[] = []
  const errors: string[] = []
  for (const raw of stdout.split(/\r?\n/)) {
    const line = raw.trim()
    const match = /^(OUTPUT|WARNING|ERROR):\s*(.*)$/.exec(line)
    if (!match || !match[2]) continue
    if (match[1] === "OUTPUT") outputs.push(match[2])
    else if (match[1] === "WARNING") warnings.push(match[2])
    else errors.push(match[2])
  }
  return { outputs, warnings, errors }
}

/** yyyyMMddHHmmss in local time, to keep each run's documents apart. */
export const exportTimestamp = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0")
  return (
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
    `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
  )
}

const isMapFile = (name: string): boolean => name.toLowerCase().endsWith(".map.csv")

/**
 * The name of a script's output in the download: the export's filename with the output's
 * extensions, e.g. `Automation_Output/Wave1_20261028120005_ascot.map.xlsx` -> `Wave1.map.xlsx`.
 * `stem` is the name the export was uploaded under; an output without extensions keeps its name.
 */
export const downloadEntryName = (path: string, stem: string, filename: string): string => {
  const base = path.slice(path.lastIndexOf("/") + 1)
  const rest = base.startsWith(stem) ? base.slice(stem.length) : base
  const dot = rest.indexOf(".")
  return dot < 0 ? base : `${filename}${rest.slice(dot)}`
}

/** The data and map files of a CSV export's zip. Throws unless it holds exactly one of each. */
export const splitExportZip = async (exportZip: Blob): Promise<{ data: Blob; map: Blob }> => {
  const zip = await JSZip.loadAsync(exportZip)
  const files = Object.values(zip.files).filter((f) => !f.dir)
  const maps = files.filter((f) => isMapFile(f.name))
  const data = files.filter((f) => f.name.toLowerCase().endsWith(".csv") && !isMapFile(f.name))
  if (maps.length !== 1 || data.length !== 1) {
    throw new Error(
      `The CSV export should hold one data file and one map file, but it holds ${data.length} data and ${maps.length} map files.`
    )
  }
  return { data: await data[0].async("blob"), map: await maps[0].async("blob") }
}

/** The server's error message in a reply that came back as a success, if any. */
const replyError = (data: unknown): string | null => {
  if (typeof data === "object" && data !== null && "Err msg" in data) {
    return String((data as Record<string, unknown>)["Err msg"])
  }
  return null
}

/** Deletes documents, best effort: a failed delete is logged, never reported as a failed export. */
const deleteDocuments = async (
  axios: AxiosInstance,
  projectId: string,
  owner: string,
  paths: string[]
): Promise<void> => {
  await Promise.all(
    paths.map(async (path) => {
      try {
        await documentDelete(axios, projectId, owner, path)
      } catch (error) {
        console.error(`Could not delete ${path} after the export:`, error)
      }
    })
  )
}

interface RunExportScriptArgs {
  axios: AxiosInstance
  scriptName: string
  /** The zip of the normal CSV export with the export map (data + map). */
  exportZip: Blob
  /** The user's filename, without extension. */
  filename: string
  projectId: string
  owner: string
  now?: Date
  pollIntervalMs?: number
  timeoutMs?: number
  sleep?: (ms: number) => Promise<void>
}

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

/** Polls the job until it ends; cancels it and throws when it runs past the timeout. */
const waitForJob = async (
  axios: AxiosInstance,
  jobId: string,
  pollIntervalMs: number,
  timeoutMs: number,
  sleep: (ms: number) => Promise<void>
): Promise<JobStatusResponse> => {
  for (let waited = 0; ; waited += pollIntervalMs) {
    const status = (await getJobStatus(axios, jobId)).data
    if (status && isJobCompleted(status.status)) return status
    if (waited >= timeoutMs) {
      await cancelJob(axios, jobId).catch(() => undefined)
      throw new Error(`The script did not finish within ${Math.round(timeoutMs / 60_000)} minutes.`)
    }
    await sleep(pollIntervalMs)
  }
}

/**
 * Runs an export script on a CSV export and downloads what it wrote as `<filename>.zip`
 * (`<filename>.csv`, `<filename>.map.csv` and whatever else it wrote). Uploads the export's two files, runs the script on
 * the data file, waits for it, fetches the files its OUTPUT lines name, then deletes every
 * document it made (the inputs also when the script fails). Throws with the script's ERROR lines
 * when it fails.
 */
export const runExportScript = async ({
  axios,
  scriptName,
  exportZip,
  filename,
  projectId,
  owner,
  now = new Date(),
  pollIntervalMs = POLL_INTERVAL_MS,
  timeoutMs = TIMEOUT_MS,
  sleep = wait,
}: RunExportScriptArgs): Promise<ExportScriptResult> => {
  const { data, map } = await splitExportZip(exportZip)
  const stem = `${filename}_${exportTimestamp(now)}`
  const inputs = [
    { name: `${stem}.csv`, content: data },
    { name: `${stem}.map.csv`, content: map },
  ]
  // Every document made, deleted at the end whatever happens
  const made: string[] = []

  try {
    // The folder may not exist yet; creating it fails when it does, which is fine
    await documentFolderCreate(axios, projectId, owner, EXPORT_SCRIPT_FOLDER).catch(() => undefined)
    for (const input of inputs) {
      const reply = await documentUpload(axios, projectId, owner, input.name, input.content, EXPORT_SCRIPT_FOLDER)
      const error = replyError(reply.data)
      if (error) throw new Error(error)
      made.push(`${EXPORT_SCRIPT_FOLDER}/${input.name}`)
    }

    const run = await runAutomationScript(
      axios,
      scriptName,
      { input_file: `${EXPORT_SCRIPT_FOLDER}/${inputs[0].name}` },
      { project_id: projectId, owner }
    )
    const jobId = run.data?.result?.job_id
    if (!jobId) throw new Error(replyError(run.data) ?? "The script could not be started.")

    const job = await waitForJob(axios, jobId, pollIntervalMs, timeoutMs, sleep)
    const report = parseScriptOutput(job.stdout_output)
    // Whatever the script says it wrote is removed too, also when it then fails
    made.push(...report.outputs)
    const failed =
      job.status !== JOB_STATUS.COMPLETED || (job.exit_code ?? 0) !== 0 || report.errors.length > 0
    if (failed) {
      throw new Error(
        report.errors.length > 0
          ? report.errors.join("\n")
          : // A script that crashed has no ERROR line; what it printed to stderr says why
            [`The script ended with status ${job.status}.`, job.stderr_output?.trim()]
              .filter(Boolean)
              .join("\n")
      )
    }
    if (report.outputs.length === 0) throw new Error("The script did not say which files it wrote.")

    const zip = new JSZip()
    for (const path of report.outputs) {
      const { blob } = await documentFetch(axios, projectId, owner, path)
      const name = downloadEntryName(path, stem, filename)
      if (zip.file(name)) throw new Error(`The script wrote more than one ${name}.`)
      zip.file(name, blob, { compression: "DEFLATE" })
    }
    const content = await zip.generateAsync({ type: "blob", compression: "DEFLATE" })
    const file = downloadDataAsFile(content, `${filename}.zip`, "application/zip")
    if (!file) throw new Error("The file could not be saved.")
    return { file, warnings: report.warnings }
  } finally {
    await deleteDocuments(axios, projectId, owner, made)
  }
}

/** The unit's export scripts, read once per dialog opening; a failure lists none. */
export const useExportScriptsQuery = (enabled: boolean): UseQueryResult<ExportScript[]> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.automation.exportScripts,
    enabled,
    staleTime: 5 * 60_000,
    meta: { silent: true },
    queryFn: async (): Promise<ExportScript[]> => loadExportScripts(await getAxios()),
  })
}

/**
 * The unit's export scripts and a runner for one on the current project: shows that it is
 * running, the script's warnings after the download and its error when it fails (then throws).
 */
export const useExportScripts = (enabled: boolean) => {
  const getAxios = useAuthedAxios()
  const project = useProjectStore((s) => s.project)
  const { data: exportScripts = [] } = useExportScriptsQuery(enabled)

  const runScriptExport = useCallback(
    async (script: ExportScript, exportZip: Blob, filename: string): Promise<File> => {
      const done = notify.toast.loading(`Running ${script.label}...`)
      try {
        const { file, warnings } = await runExportScript({
          axios: await getAxios(),
          scriptName: script.name,
          exportZip,
          filename,
          projectId: project.id,
          owner: project.Created_by,
        })
        done()
        notify.toast.success(`${script.label} export completed successfully`)
        if (warnings.length > 0) notify.toast.warning(`${script.label}: ${warnings.join("; ")}`, 10)
        return file
      } catch (error) {
        done()
        notify.error(
          `${script.label} export failed`,
          error instanceof Error ? error.message : "Unknown error occurred"
        )
        throw error
      }
    },
    [getAxios, project.id, project.Created_by]
  )

  return { exportScripts, runScriptExport }
}
